import { Injectable } from '@angular/core';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { LeaderboardState, ModuleSubjects, RankingModuleData } from './leaderboard.model';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LeaderboardService {

    private leaderboardStatesMap = new Map<string, LeaderboardState>();
    private leaderboardSubjectsMap = new Map<string, ModuleSubjects>();

    constructor(
      private cvApiService: CVApiService
    ) { }

    public async LoadRankingModuleData(moduleKey: string, isAutoRefresh: boolean = false, isFirstLoad: boolean = false, abort?: Promise<void>): Promise<RankingModuleData> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
        }

        try {
            const url = `/api/v2/dashboards/modules/ranking/${encodeURIComponent(moduleKey)}?isAutoRefresh=${isAutoRefresh}&isFirstLoad=${isFirstLoad}`;
            const response = await this.cvApiService.get<RankingModuleData>(url, renderEndpoint, abort);
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        } catch (err) {
            if (!(err instanceof DOMException && err.name === "AbortError"))
                console.error("Failed getting data for leaderboard module");
            throw err;
        }
    }

    public async LoadRankingModuleDataForExport(moduleKey: string, format: ModuleManagerExportFormat = ModuleManagerExportFormat.Excel): Promise<ApiResponse<string>> {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (err) {
            console.error("Failed getting module render endpoint");
        }

        try {
            const url = `/api/v2/dashboards/modules/ranking/${encodeURIComponent(moduleKey)}/export?format=${encodeURIComponent(format)}`;
            const response = await this.cvApiService.get<string>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed getting data to export for leaderboard module");
            throw err;
        }
    }

    public getHasCurrentMetric(moduleKey: string): Observable<boolean> {
        return this.getOrCreateSubjects(moduleKey).hasCurrentMetric.asObservable();
    }

    public getIsCurrentUserInData(moduleKey: string): Observable<boolean> {
        return this.getOrCreateSubjects(moduleKey).isCurrentUserInData.asObservable();
    }

    public getLeaderboardStates(moduleKey: string): LeaderboardState {
        if (!this.leaderboardStatesMap.has(moduleKey)) {
            this.leaderboardStatesMap.set(moduleKey, {
                hasCurrentMetric: false,
                isCurrentUserInData: false
            });
        }
        return this.leaderboardStatesMap.get(moduleKey);
    }
    
    public setLeaderboardStates(hasCurrentMetric: boolean, isCurrentUserInData: boolean, moduleKey: string) {
        this.leaderboardStatesMap.set(moduleKey, {
            hasCurrentMetric,
            isCurrentUserInData
        });

        const subjects = this.getOrCreateSubjects(moduleKey);
        subjects.hasCurrentMetric.next(hasCurrentMetric);
        subjects.isCurrentUserInData.next(isCurrentUserInData);
    }

    private getOrCreateSubjects(moduleKey: string): ModuleSubjects {
        if (!this.leaderboardSubjectsMap.has(moduleKey)) {
            this.leaderboardSubjectsMap.set(moduleKey, {
                hasCurrentMetric: new BehaviorSubject<boolean>(false),
                isCurrentUserInData: new BehaviorSubject<boolean>(false)
            });
        }
        return this.leaderboardSubjectsMap.get(moduleKey);
    }
}
